/* Triumph DATA PLATE | tl-global.js
   Shared runtime for every page (loaded with defer from layout/theme.liquid, after the inline
   window.TL bootstrap that sets TL.routes, TL.moneyFormat, TL.settings, TL.store, TL.strings).
   Vanilla ES2017, no dependencies.

   window.TL API
     TL.formatMoney(cents[, format])        -> string (store money format; may contain HTML if the format does)
     TL.formatMoneyText(cents[, format])    -> plain-text version (tags stripped, entities decoded)
     TL.cart.get()                          -> Promise<cart>
     TL.cart.add(formOrFormDataOrObject)    -> Promise<item>  POST /cart/add.js, then GET /cart.js, then
                                               document events 'tl:cart:added' {item, cart, source} and
                                               'tl:cart:updated' {cart}. cartType 'page' redirects to the cart
                                               instead of firing 'tl:cart:added'. Rejects with an Error whose
                                               message is Shopify's description.
     TL.cart.change(key, quantity)          -> Promise<cart>  POST /cart/change.js, fires 'tl:cart:updated'
     TL.cart.update(updates)                -> Promise<cart>  POST /cart/update.js {updates}, fires 'tl:cart:updated'
     TL.cart.last                           -> the last cart object fetched (or null)
     TL.trapFocus(container[, elementToFocus]) / TL.removeTrapFocus([elementToFocusAfter])
     TL.getFocusable(container)             -> visible focusable elements
     TL.lockScroll(on)                      -> counted page scroll lock (html.tl-scroll-lock)
     TL.debounce(fn, ms)
     TL.announce(message)                   -> polite screen-reader announcement
     TL.emit(name, detail)                  -> document CustomEvent (bubbles)
     TL.onSections(selector, init[, destroy]) -> runs init(el) for every match on DOMContentLoaded and on
                                               shopify:section:load (destroy(el) on shopify:section:unload);
                                               guards double init with el.dataset.tlInit
   Automatic behaviour
     form[data-tl-product-form] / any form containing [data-tl-atc]: submit is intercepted -> TL.cart.add(form);
       busy state on the button (class is-busy + aria-busy, label in [data-tl-atc-label] -> TL.strings.adding);
       errors shown in [data-tl-form-error] inside the form (created if missing). A listener that calls
       event.preventDefault() on the form first keeps full control (this handler then skips it).
     [data-tl-cart-count] elements get cart.item_count on 'tl:cart:updated' ([data-hide-zero] -> hidden at 0);
       [data-tl-cart-aria] elements get aria-label = that attribute with {count} replaced.
     .tl-wm__img logo that fails to load -> removed, parent .tl-wm loses has-img (typographic wordmark shows).
*/
(function () {
  'use strict';

  var TL = window.TL = window.TL || {};
  TL.routes = TL.routes || {};
  TL.settings = TL.settings || {};
  TL.strings = TL.strings || {};

  function route(key, fallback) {
    return TL.routes[key] || fallback;
  }
  function str(key, fallback) {
    return TL.strings[key] || fallback;
  }

  /* ── events ─────────────────────────────────────────────────────────── */
  TL.emit = function (name, detail) {
    var ev;
    try {
      ev = new CustomEvent(name, { bubbles: true, detail: detail });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, true, false, detail);
    }
    document.dispatchEvent(ev);
    return ev;
  };

  /* ── utilities ──────────────────────────────────────────────────────── */
  TL.debounce = function (fn, ms) {
    var t = null;
    return function () {
      var self = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, ms || 0);
    };
  };

  TL.prefersReducedMotion = function () {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  };

  var live = null;
  TL.announce = function (message) {
    if (!document.body) return;
    if (!live || !live.isConnected) {
      live = document.createElement('div');
      live.className = 'tl-sr';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('role', 'status');
      document.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(function () { live.textContent = String(message || ''); }, 60);
  };

  /* ── money ──────────────────────────────────────────────────────────── */
  function withDelimiters(cents, precision, thousands, decimal) {
    var neg = cents < 0;
    var num = (Math.abs(cents) / 100).toFixed(precision);
    var parts = num.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    var out = parts[1] ? whole + decimal + parts[1] : whole;
    return neg ? '-' + out : out;
  }

  TL.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10);
    if (isNaN(cents)) cents = 0;
    var fmt = format || TL.moneyFormat || '${{amount}}';
    var re = /\{\{\s*(\w+)\s*\}\}/;
    var m = fmt.match(re);
    if (!m) return fmt;
    var v;
    switch (m[1]) {
      case 'amount_no_decimals': v = withDelimiters(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': v = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': v = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': v = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': v = withDelimiters(cents, 0, ' ', ','); break;
      case 'amount_with_period_and_space_separator': v = withDelimiters(cents, 2, ' ', '.'); break;
      default: v = withDelimiters(cents, 2, ',', '.');
    }
    return fmt.replace(re, v);
  };

  TL.formatMoneyText = function (cents, format) {
    var tmp = document.createElement('div');
    tmp.innerHTML = TL.formatMoney(cents, format);
    return (tmp.textContent || '').trim();
  };

  /* ── cart ───────────────────────────────────────────────────────────── */
  function request(url, opts) {
    opts = opts || {};
    var headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    if (opts.json) headers['Content-Type'] = 'application/json';
    return fetch(url, {
      method: opts.method || 'GET',
      headers: headers,
      body: opts.body,
      credentials: 'same-origin',
      cache: opts.method && opts.method !== 'GET' ? 'no-store' : 'no-cache'
    }).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
        if (!res.ok) {
          var msg = '';
          if (data) {
            if (typeof data.description === 'string' && data.description) msg = data.description;
            else if (typeof data.message === 'string' && data.message) msg = data.message;
            else if (data.errors) msg = typeof data.errors === 'string' ? data.errors : '';
          }
          var err = new Error(msg || str('error', 'This item could not be added to the cart. Please try again.'));
          err.status = res.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function cartUpdated(cart) {
    TL.cart.last = cart;
    TL.emit('tl:cart:updated', { cart: cart });
    return cart;
  }

  TL.cart = {
    last: null,

    get: function () {
      return request(route('cart_js', route('cart', '/cart') + '.js')).then(function (cart) {
        TL.cart.last = cart;
        return cart;
      });
    },

    add: function (source) {
      var body, json = false;
      if (typeof HTMLFormElement !== 'undefined' && source instanceof HTMLFormElement) {
        body = new FormData(source);
      } else if (typeof FormData !== 'undefined' && source instanceof FormData) {
        body = source;
      } else if (source && typeof source === 'object') {
        body = JSON.stringify(source);
        json = true;
      } else {
        return Promise.reject(new Error(str('error', 'This item could not be added to the cart. Please try again.')));
      }
      return request(route('cart_add', '/cart/add.js'), { method: 'POST', body: body, json: json })
        .then(function (item) {
          return TL.cart.get().then(function (cart) {
            if (TL.settings.cartType === 'page') {
              cartUpdated(cart);
              window.location.href = route('cart', '/cart');
              return item;
            }
            TL.emit('tl:cart:added', { item: item, cart: cart, source: source });
            cartUpdated(cart);
            return item;
          });
        });
    },

    change: function (key, quantity) {
      var payload = { id: String(key), quantity: Math.max(0, parseInt(quantity, 10) || 0) };
      return request(route('cart_change', '/cart/change.js'), { method: 'POST', body: JSON.stringify(payload), json: true })
        .then(cartUpdated);
    },

    update: function (updates) {
      return request(route('cart_update', '/cart/update.js'), { method: 'POST', body: JSON.stringify({ updates: updates || {} }), json: true })
        .then(cartUpdated);
    }
  };

  /* cart count badges */
  function paintCount(cart) {
    if (!cart) return;
    var n = parseInt(cart.item_count, 10) || 0;
    var els = document.querySelectorAll('[data-tl-cart-count]');
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = String(n);
      if (els[i].hasAttribute('data-hide-zero')) els[i].hidden = n === 0;
    }
    var labelled = document.querySelectorAll('[data-tl-cart-aria]');
    for (var j = 0; j < labelled.length; j++) {
      var tpl = labelled[j].getAttribute('data-tl-cart-aria') || '';
      if (tpl) labelled[j].setAttribute('aria-label', tpl.replace(/\{count\}/g, String(n)));
    }
  }
  document.addEventListener('tl:cart:updated', function (e) {
    paintCount(e.detail && e.detail.cart);
  });
  /* back/forward cache: the page may show a stale count */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) TL.cart.get().then(cartUpdated).catch(function () {});
  });

  /* ── product forms (cards, product page, sticky bar) ────────────────── */
  function formError(form, btn, message) {
    var box = form.querySelector('[data-tl-form-error]');
    if (!box && btn && btn.closest) {
      var scope = btn.closest('[data-tl-card], .shopify-section');
      if (scope) box = scope.querySelector('[data-tl-form-error]');
    }
    if (!box && message) {
      box = document.createElement('p');
      box.className = 'tl-form-error';
      box.setAttribute('data-tl-form-error', '');
      box.setAttribute('role', 'alert');
      form.appendChild(box);
    }
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  }

  function setBusy(btn, busy) {
    if (!btn) return;
    var label = btn.querySelector('[data-tl-atc-label]');
    if (busy) {
      btn.classList.add('is-busy');
      btn.setAttribute('aria-busy', 'true');
      if (label) {
        if (!label.hasAttribute('data-tl-label')) label.setAttribute('data-tl-label', label.textContent);
        label.textContent = str('adding', 'Adding…');
      }
    } else {
      btn.classList.remove('is-busy');
      btn.removeAttribute('aria-busy');
      if (label && label.hasAttribute('data-tl-label')) label.textContent = label.getAttribute('data-tl-label');
    }
  }

  function flashAdded(btn) {
    var label = btn && btn.querySelector('[data-tl-atc-label]');
    if (!label) return;
    var original = label.getAttribute('data-tl-label') || label.textContent;
    label.textContent = str('added', 'Added');
    setTimeout(function () {
      if (!btn.classList.contains('is-busy')) label.textContent = original;
    }, 1600);
  }

  function isProductForm(form) {
    if (!form || form.nodeName !== 'FORM') return false;
    if (form.hasAttribute('data-tl-product-form')) return true;
    if (form.querySelector('[data-tl-atc]')) return true;
    return !!(form.id && document.querySelector('[data-tl-atc][form="' + form.id + '"]'));
  }

  function submitProductForm(form, btn) {
    if (form.getAttribute('data-tl-busy') === 'true') return;
    if (!btn) {
      btn = form.querySelector('[data-tl-atc]') ||
        (form.id && document.querySelector('[data-tl-atc][form="' + form.id + '"]')) ||
        form.querySelector('[type="submit"]');
    }
    if (btn && (btn.disabled || btn.getAttribute('aria-disabled') === 'true')) return;
    form.setAttribute('data-tl-busy', 'true');
    formError(form, btn, '');
    setBusy(btn, true);
    TL.cart.add(form).then(function () {
      setBusy(btn, false);
      if (TL.settings.cartType !== 'page') flashAdded(btn);
    }).catch(function (err) {
      setBusy(btn, false);
      formError(form, btn, (err && err.message) || str('error', 'This item could not be added to the cart. Please try again.'));
    }).then(function () {
      form.removeAttribute('data-tl-busy');
    });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (e.defaultPrevented || !isProductForm(form)) return;
    if (typeof window.fetch !== 'function') return; /* very old browser: normal POST to /cart/add */
    e.preventDefault();
    var btn = e.submitter && e.submitter.hasAttribute && e.submitter.hasAttribute('data-tl-atc') ? e.submitter : null;
    submitProductForm(form, btn);
  });

  /* [data-tl-atc] buttons with type="button" (e.g. a sticky bar outside the form, using form="id") */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('button[data-tl-atc][type="button"]') : null;
    if (!btn || e.defaultPrevented) return;
    var form = btn.form || btn.closest('form');
    if (!form || typeof window.fetch !== 'function') return;
    e.preventDefault();
    submitProductForm(form, btn);
  });

  /* ── focus management ───────────────────────────────────────────────── */
  var FOCUSABLE = 'summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^="-"]), [draggable="true"], area[href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), object, iframe, [contenteditable="true"]';

  TL.getFocusable = function (container) {
    if (!container) return [];
    var list = container.querySelectorAll(FOCUSABLE), out = [];
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (el.offsetWidth || el.offsetHeight || el.getClientRects().length) out.push(el);
    }
    return out;
  };

  var trap = { container: null, keydown: null, focusin: null };

  TL.removeTrapFocus = function (elementToFocusAfter) {
    if (trap.keydown) document.removeEventListener('keydown', trap.keydown, true);
    if (trap.focusin) document.removeEventListener('focusin', trap.focusin, true);
    trap.container = trap.keydown = trap.focusin = null;
    if (elementToFocusAfter && typeof elementToFocusAfter.focus === 'function') {
      try { elementToFocusAfter.focus({ preventScroll: true }); } catch (e) { elementToFocusAfter.focus(); }
    }
  };

  TL.trapFocus = function (container, elementToFocus) {
    if (!container) return;
    TL.removeTrapFocus();
    trap.container = container;
    trap.keydown = function (e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      var els = TL.getFocusable(container);
      if (!els.length) { e.preventDefault(); container.focus(); return; }
      var first = els[0], last = els[els.length - 1], active = document.activeElement;
      if (e.shiftKey && (active === first || active === container || !container.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !container.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    trap.focusin = function (e) {
      if (!container.contains(e.target)) {
        var els = TL.getFocusable(container);
        (els[0] || container).focus();
      }
    };
    document.addEventListener('keydown', trap.keydown, true);
    document.addEventListener('focusin', trap.focusin, true);
    var target = elementToFocus || TL.getFocusable(container)[0] || container;
    if (target === container && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
    try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
  };

  /* ── scroll lock (counted, so nested drawers/dialogs can each lock and unlock) ── */
  var locks = 0;
  TL.lockScroll = function (on) {
    var root = document.documentElement;
    if (on) {
      if (locks === 0) {
        var sbw = window.innerWidth - root.clientWidth;
        root.style.setProperty('--tl-sbw', (sbw > 0 ? sbw : 0) + 'px');
        root.classList.add('tl-scroll-lock');
      }
      locks++;
    } else {
      locks = Math.max(0, locks - 1);
      if (locks === 0) {
        root.classList.remove('tl-scroll-lock');
        root.style.removeProperty('--tl-sbw');
      }
    }
    return locks > 0;
  };

  /* ── section init helper (DOMContentLoaded + theme editor) ──────────── */
  TL.onSections = function (selector, init, destroy) {
    function run(scope) {
      var root = scope || document;
      var els = [];
      if (root.matches && root.matches(selector)) els.push(root);
      var found = root.querySelectorAll(selector);
      for (var i = 0; i < found.length; i++) els.push(found[i]);
      els.forEach(function (el) {
        if (el.dataset.tlInit) return;
        el.dataset.tlInit = '1';
        try { init(el); } catch (err) { if (window.console) console.error(err); }
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { run(document); });
    } else {
      run(document);
    }
    document.addEventListener('shopify:section:load', function (e) { run(e.target); });
    document.addEventListener('shopify:section:unload', function (e) {
      var found = e.target.querySelectorAll(selector);
      for (var i = 0; i < found.length; i++) {
        if (destroy) { try { destroy(found[i]); } catch (err) { if (window.console) console.error(err); } }
        delete found[i].dataset.tlInit;
      }
    });
  };

  /* ── wordmark logo image fallback ───────────────────────────────────── */
  function dropLogo(img) {
    var a = img.closest ? img.closest('.tl-wm') : null;
    if (a) a.classList.remove('has-img');
    if (img.parentNode) img.parentNode.removeChild(img);
  }
  /* error events do not bubble: listen in the capture phase (covers sections loaded later too) */
  document.addEventListener('error', function (e) {
    var t = e.target;
    if (t && t.nodeName === 'IMG' && t.classList && t.classList.contains('tl-wm__img')) dropLogo(t);
  }, true);
  /* an image that already failed before this script ran: re-test it (an SVG can report naturalWidth 0) */
  function checkLogos() {
    var imgs = document.querySelectorAll('.tl-wm__img');
    Array.prototype.forEach.call(imgs, function (img) {
      if (!img.complete || img.naturalWidth !== 0 || !img.getAttribute('src')) return;
      var probe = new Image();
      probe.onerror = function () { dropLogo(img); };
      probe.src = img.currentSrc || img.src;
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', checkLogos);
  else checkLogos();
  document.addEventListener('shopify:section:load', checkLogos);
})();
